import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SharePopup from "../../components/ui/SharePopup/SharePopup";
import {
    fetchPostById,
    selectPostDetail,
    updatePostAsync,
    deletePostAsync,
    Comment,
} from "../../store/slices/newsfeedSlice";
import { RootState } from "../../store/store";
import { selectProfilePhoto } from "../../store/slices/profileSlice";
import { fetchProfile, selectUserProfileById } from "../../store/slices/profileSlice";
import {
    Box,
    Typography,
    Paper,
    Avatar,
    Grid,
    Divider,
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    IconButton,
} from "@mui/material";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import TimeAgo from "timeago-react";
import styles from "./PostDetail.module.scss";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReadMoreText from "../../components/ui/read-more-textbox/ReadMoreText";
import MediaGrid from "../../components/media/MediaGrid/MediaGrid";
import { getAbsoluteImageUrl } from "../../utils/imageUtils";
import { AppDispatch } from "../../store/store";
import DeletePostModal from "../../components/modal/DeletePostModal/DeletePostModal";

interface MediaItem {
    mediaType: "image" | "video";
    mediaUrl: string;
    thumbnailUrl?: string;
}

const PostDetail: React.FC = () => {
    const navigate = useNavigate();

    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch<AppDispatch>();
    const { postId } = useParams<{ postId: string }>();

    const {
        postDetail: post,
        loading,
        error,
    } = useSelector((state: RootState) => ({
        postDetail: selectPostDetail(state),
        loading: state.newsfeed.loading,
        error: state.newsfeed.error,
    }));

    const userProfilePhoto = useSelector(selectProfilePhoto);

    const authorId = post?.author?.authorId;
    useEffect(() => {
        if (authorId) {
            dispatch(fetchProfile(authorId));
        }
    }, [dispatch, authorId]);

    const authorProfile = useSelector((state: RootState) => {
        if (authorId) {
            return selectUserProfileById(state, authorId);
        }
        return null;
    });

    const [commentText, setCommentText] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [shareAnchorEl, setShareAnchorEl] = useState<HTMLElement | null>(null);
    const currentURL = window.location.href;
    const [commentError, setCommentError] = useState<boolean>(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement | SVGSVGElement>(null);
    const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);

    const [postMenuAnchorEl, setPostMenuAnchorEl] = useState<null | HTMLElement>(null);

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editCommentId, setEditCommentId] = useState<string | null>(null);
    const [editCommentText, setEditCommentText] = useState<string>("");

    const [liked, setLiked] = useState(false);
    const [isCommentLoading, setIsCommentLoading] = useState<boolean>(false);

    const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const primaryColor = "var(--primary-color)";
    const primaryDarkColor = "var(--primary-dark)";

    const refreshPostData = useCallback(() => {
        if (postId && isAuthenticated) {
            dispatch(fetchPostById(postId));
        }
    }, [dispatch, postId, isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            if (postId) {
                dispatch(fetchPostById(postId));
            }
        } else {
            navigate("/auth");
        }
    }, [dispatch, postId, isAuthenticated, navigate]);

    useEffect(() => {
        if (post && user) {
            // Assuming post.likedBy array or similar structure
            // For now, we'll just keep it simple, this needs actual data structure
            // setLiked(post.likedBy?.includes(user.id) || false);
        }
    }, [post, user]);

    const handleBackClick = () => navigate("/newsfeed");

    const handlePostImageClick = (media: MediaItem[] | undefined, index: number) => {
        // No longer needed as PostImages handles this internally
    };

    const handleMenuOpen = (
        event: React.MouseEvent<SVGSVGElement | HTMLElement>,
        commentId: string,
    ) => {
        setAnchorEl(event.currentTarget as HTMLElement);
        setSelectedCommentId(commentId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedCommentId(null);
    };

    const handlePostMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setPostMenuAnchorEl(event.currentTarget);
    };

    const handlePostMenuClose = () => {
        setPostMenuAnchorEl(null);
    };

    const handleEditClick = () => {
        const commentToEdit = post?.comments.find(
            (comment: Comment) => comment.commentId === selectedCommentId,
        );
        if (commentToEdit) {
            setEditCommentId(selectedCommentId);
            setEditCommentText(commentToEdit.text || "");
            setIsEditing(true);
        }
        handleMenuClose();
    };

    const handleEditCancel = () => {
        setIsEditing(false);
        setEditCommentId(null);
        setEditCommentText("");
    };

    const handleEditSave = async () => {
        if (!post || !editCommentId || !editCommentText.trim() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            const updatedComments = post.comments.map((comment: Comment) =>
                comment.commentId === editCommentId
                    ? {
                          ...comment,
                          text: editCommentText.trim(),
                          isEdited: true,
                          timestamp: new Date().toISOString(),
                      }
                    : comment,
            );
            const updatedPost = {
                id: post.id,
                comments: updatedComments,
                engagement: post.engagement,
            };
            await dispatch(updatePostAsync(updatedPost));
            handleEditCancel();
        } catch (err) {
            console.error("Failed to edit comment:", err);
            // TODO: Show error to user
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddComment = async () => {
        if (!postId || !commentText.trim() || isSubmitting) {
            if (!commentText.trim()) setCommentError(true);
            return;
        }
        setIsSubmitting(true);
        setIsCommentLoading(true);
        try {
            const newComment: Comment = {
                commentId: Date.now().toString(),
                author: {
                    authorId: user?.id || "temp-user-id",
                    name:
                        user?.firstName && user?.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user?.firstName || "MAX Aspire User",
                    profileUrl: user ? `/profile/${user.id}` : "/profile/unknown",
                    profileImageUrl: userProfilePhoto || "/images/default-avatar.png",
                },
                text: commentText.trim(),
                timestamp: new Date().toISOString(),
                isEdited: false,
            };
            if (!post) {
                console.error("Post not found for adding comment");
                setIsCommentLoading(false);
                setIsSubmitting(false);
                return;
            }
            const existingComments = Array.isArray(post.comments) ? post.comments : [];
            const updatedPost = {
                id: post.id,
                comments: [...existingComments, newComment],
                engagement: {
                    ...post.engagement,
                    commentsCount: (post.engagement?.commentsCount || 0) + 1,
                },
            };
            await dispatch(updatePostAsync(updatedPost));
            setCommentText("");
            setCommentError(false);
        } catch (err) {
            console.error("Failed to add comment:", err);
            // TODO: Show error to user
        } finally {
            setIsCommentLoading(false);
            setIsSubmitting(false);
        }
    };

    const handleOpenDeleteDialog = (commentId: string) => {
        setCommentToDelete(commentId);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
    };

    const handleDeleteComment = async () => {
        if (!commentToDelete || !post || isSubmitting) return;
        setIsSubmitting(true);
        try {
            const updatedComments = (post.comments as Comment[]).filter(
                (comment) => comment.commentId !== commentToDelete,
            );
            const updatedPost = {
                id: post.id,
                comments: updatedComments,
                engagement: {
                    ...post.engagement,
                    commentsCount: Math.max(0, (post.engagement?.commentsCount || 0) - 1),
                },
            };
            await dispatch(updatePostAsync(updatedPost));
            handleCloseDeleteDialog();
        } catch (err) {
            console.error("Failed to delete comment:", err);
            // TODO: Show error to user
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLikePostClick = async () => {
        if (!post || isSubmitting) return;
        const newLikedState = !liked;
        setLiked(newLikedState);

        const likeChange = newLikedState ? 1 : -1;
        const currentLikes = post.engagement?.likesCount || 0;

        try {
            const updatedPost = {
                id: post.id,
                engagement: {
                    ...post.engagement,
                    likesCount: Math.max(0, currentLikes + likeChange),
                },
            };
            await dispatch(updatePostAsync(updatedPost));
        } catch (error) {
            console.error("Failed to update like:", error);
            setLiked(!newLikedState);
        }
    };

    const handleShareClick = (event: React.MouseEvent<HTMLElement>) =>
        setShareAnchorEl(event.currentTarget);
    const handleCloseSharePopover = () => setShareAnchorEl(null);

    const authorAvatarUrl = useMemo(
        () => getAbsoluteImageUrl(post?.author?.profileImageUrl || ""),
        [post?.author?.profileImageUrl],
    );

    const handleOpenDeletePostDialog = () => {
        setDeleteModalOpen(true);
        handlePostMenuClose();
    };

    const handleConfirmDeletePost = async () => {
        if (!postId) return;
        setIsDeleting(true);
        try {
            await dispatch(deletePostAsync(postId)).unwrap();
            navigate("/newsfeed");
            // You can show a success toast here
        } catch (error) {
            console.error("Failed to delete post:", error);
            // You can show an error toast here
        } finally {
            setIsDeleting(false);
            setDeleteModalOpen(false);
        }
    };

    if (loading && !post) {
        return (
            <Box className={styles.loadingContainer}>
                <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{ color: primaryColor, marginBottom: 2 }}
                />
                <Typography variant="h6" sx={{ color: "var(--text-color)" }}>
                    Loading post details...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            // TODO: Make this a full 100% width
            <Box
                // sx={{ width: "100%", border: "5px solid red !important" }}
                className={styles.pageWrapper}
            >
                <Paper
                    className={styles.postDetailCard}
                    elevation={0}
                    sx={{ padding: "2rem", textAlign: "center" }}
                >
                    <Typography variant="h5" color="var(--error-500)" gutterBottom>
                        Error
                    </Typography>
                    <Typography color="var(--text-secondary-color)">
                        Could not load post details. Please try again later.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={refreshPostData}
                        sx={{ mt: 2, color: primaryColor, borderColor: primaryColor }}
                    >
                        Try Again
                    </Button>
                </Paper>
            </Box>
        );
    }

    if (!post && !loading) {
        return (
            <Box className={styles.pageWrapper}>
                <Paper
                    className={styles.postDetailCard}
                    elevation={0}
                    sx={{ padding: "2rem", textAlign: "center" }}
                >
                    <Typography variant="h5" color="var(--text-color)" gutterBottom>
                        Post Not Found
                    </Typography>
                    <Typography color="var(--text-secondary-color)">
                        The post you are looking for does not exist or may have been removed.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleBackClick}
                        sx={{
                            mt: 2,
                            bgcolor: primaryColor,
                            "&:hover": { bgcolor: primaryDarkColor },
                        }}
                    >
                        Back to Feed
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box className={styles.pageWrapper}>
            {isCommentLoading && (
                <Box className={styles.commentLoadingOverlay}>
                    <CircularProgress
                        size={40}
                        thickness={4}
                        sx={{ color: primaryColor, marginBottom: 2 }}
                    />
                    <Typography variant="body1" sx={{ color: "var(--text-color)" }}>
                        Posting comment...
                    </Typography>
                </Box>
            )}

            <Box className={styles.topNavBar}>
                <Button
                    variant="text"
                    onClick={handleBackClick}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                        color: "var(--text-secondary-color)",
                        fontFamily: '"Roboto", sans-serif',
                        textTransform: "none",
                        padding: "0.25rem 0.5rem",
                        "&:hover": { bgcolor: "var(--hover-background)" },
                    }}
                >
                    Back to Feed
                </Button>
                <Button
                    variant="outlined"
                    onClick={refreshPostData}
                    startIcon={<RefreshIcon />}
                    disabled={loading}
                    sx={{
                        color: primaryColor,
                        borderColor: primaryColor,
                        fontFamily: '"Roboto", sans-serif',
                        textTransform: "none",
                        padding: "0.25rem 0.75rem",
                        "&:hover": {
                            borderColor: primaryDarkColor,
                            color: primaryDarkColor,
                            bgcolor: `rgba(${getComputedStyle(document.documentElement).getPropertyValue("--primary-color-rgb")}, 0.08)`,
                        },
                    }}
                >
                    {loading && !isCommentLoading ? "Refreshing..." : "Refresh"}
                </Button>
            </Box>

            <Paper className={styles.postDetailCard} elevation={0}>
                <Box className={styles.accentLine} />
                <Box className={styles.contentPadding}>
                    <Box className={styles.postAuthorSection}>
                        <Avatar
                            src={authorAvatarUrl}
                            className={styles.authorAvatar}
                            onClick={() =>
                                post?.author?.authorId &&
                                navigate(`/profile/${post.author.authorId}`)
                            }
                            sx={{ cursor: "pointer" }}
                        >
                            {!post?.author?.profileImageUrl &&
                                post?.author?.name?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box className={styles.authorInfo}>
                            <Typography
                                className={styles.authorName}
                                onClick={() =>
                                    post?.author?.authorId &&
                                    navigate(`/profile/${post.author.authorId}`)
                                }
                                sx={{ cursor: "pointer" }}
                            >
                                {post?.author?.name || "Anonymous User"}
                            </Typography>
                            <Typography className={styles.authorDetails}>
                                {/* {authorProfile?.about || "New Aspire Member"} */}
                                {authorProfile?.about && authorProfile.about.length > 0 && (
                                    <>
                                        <span title={authorProfile.about}>
                                            {authorProfile.about.substring(0, 20)}
                                            {authorProfile.about.length > 20 ? "..." : ""}
                                        </span>
                                    </>
                                )}
                                {" · "}
                                <TimeAgo
                                    datetime={
                                        post?.postMetadata?.postDate || new Date().toISOString()
                                    }
                                    locale="en_US"
                                />
                            </Typography>
                        </Box>
                        {user?.id === post?.author?.authorId && (
                            <IconButton
                                aria-label="settings"
                                className={styles.postMenuIcon}
                                onClick={handlePostMenuOpen}
                            >
                                <MoreVertIcon />
                            </IconButton>
                        )}
                    </Box>

                    {/* <Typography variant="h1" className={styles.postTitle}>
                        {post?.content?.title || "Post Title Not Available"}
                    </Typography> */}

                    <ReadMoreText
                        text={post?.content?.text || "No content available."}
                        maxLines={10}
                        className={styles.postText}
                    />

                    {post?.content?.media && post.content.media.length > 0 && (
                        <Box className={styles.postImageContainer}>
                            <MediaGrid media={post.content.media} />
                        </Box>
                    )}

                    <Box className={styles.engagementBar}>
                        {/* TODO: Add like button back in */}
                        {/* <Box
                            className={`${styles.engagementItem} ${liked ? styles.liked : ""}`}
                            onClick={handleLikePostClick}
                        >
                            {liked ? (
                                <FavoriteIcon sx={{ color: primaryColor }} />
                            ) : (
                                <FavoriteBorderOutlinedIcon />
                            )}
                            <Typography component="span">
                                {post?.engagement?.likesCount || 0}
                            </Typography>
                        </Box> */}
                        <Box
                            className={styles.engagementItem}
                            onClick={() => document.getElementById("comment-input-field")?.focus()}
                        >
                            <ChatBubbleOutlineOutlinedIcon />
                            <Typography component="span">
                                {post?.engagement?.commentsCount || 0}
                            </Typography>
                        </Box>
                        <Box className={styles.engagementItem} onClick={handleShareClick}>
                            <ShareOutlinedIcon />
                            <Typography component="span">
                                {post?.engagement?.shareCount || 0}
                            </Typography>
                        </Box>
                    </Box>

                    <Box className={styles.commentsSection}>
                        {post?.comments?.length === 0 ? (
                            <Typography
                                sx={{
                                    textAlign: "center",
                                    color: "var(--text-secondary-color)",
                                    padding: "2rem 0",
                                }}
                            >
                                No comments yet. Be the first to share your thoughts!
                            </Typography>
                        ) : (
                            [...(post?.comments as Comment[])]
                                ?.sort(
                                    (a, b) =>
                                        new Date(b.timestamp).getTime() -
                                        new Date(a.timestamp).getTime(),
                                )
                                .map((comment: Comment) => (
                                    <Box key={comment.commentId} className={styles.commentItem}>
                                        <Avatar
                                            src={getAbsoluteImageUrl(
                                                comment.author?.profileImageUrl || "",
                                            )}
                                            className={styles.commentAvatar}
                                            onClick={() =>
                                                comment.author?.authorId &&
                                                navigate(`/profile/${comment.author.authorId}`)
                                            }
                                            sx={{ cursor: "pointer" }}
                                        >
                                            {!comment.author?.profileImageUrl &&
                                                comment.author?.name?.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box className={styles.commentContent}>
                                            {isEditing && editCommentId === comment.commentId ? (
                                                <>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        variant="outlined"
                                                        value={editCommentText}
                                                        onChange={(e) =>
                                                            setEditCommentText(e.target.value)
                                                        }
                                                        className={styles.editCommentTextField}
                                                        autoFocus
                                                    />
                                                    <Box className={styles.editActionsWrapper}>
                                                        <Button
                                                            onClick={handleEditCancel}
                                                            size="small"
                                                            sx={{
                                                                color: "var(--text-secondary-color)",
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            onClick={handleEditSave}
                                                            size="small"
                                                            variant="contained"
                                                            disabled={
                                                                !editCommentText.trim() ||
                                                                isSubmitting
                                                            }
                                                            sx={{
                                                                bgcolor: primaryColor,
                                                                "&:hover": {
                                                                    bgcolor: primaryDarkColor,
                                                                },
                                                                "&.Mui-disabled": {
                                                                    bgcolor:
                                                                        "rgba(var(--primary-color-rgb), 0.4)",
                                                                },
                                                            }}
                                                        >
                                                            {isSubmitting ? (
                                                                <CircularProgress
                                                                    size={16}
                                                                    color="inherit"
                                                                />
                                                            ) : (
                                                                "Save"
                                                            )}
                                                        </Button>
                                                    </Box>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        className={styles.commentAuthorName}
                                                        onClick={() =>
                                                            comment.author?.authorId &&
                                                            navigate(
                                                                `/profile/${comment.author.authorId}`,
                                                            )
                                                        }
                                                    >
                                                        {comment.author?.name || "User"}
                                                        {comment.isEdited && (
                                                            <span className={styles.editedFlag}>
                                                                (edited)
                                                            </span>
                                                        )}
                                                    </Typography>
                                                    <Typography className={styles.commentTextValue}>
                                                        {comment.text}
                                                    </Typography>
                                                    <Box className={styles.commentActions}>
                                                        <Typography
                                                            className={styles.commentTimestamp}
                                                        >
                                                            <TimeAgo
                                                                datetime={comment.timestamp}
                                                                locale="en_US"
                                                            />
                                                        </Typography>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                        {user &&
                                            comment?.author?.authorId === user.id &&
                                            !isEditing && (
                                                <IconButton
                                                    size="small"
                                                    className={styles.commentMenuIcon}
                                                    onClick={(e) =>
                                                        handleMenuOpen(e, comment.commentId)
                                                    }
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                    </Box>
                                ))
                        )}
                    </Box>

                    {isAuthenticated && (
                        <Box className={styles.addCommentBox}>
                            <Avatar
                                src={getAbsoluteImageUrl(userProfilePhoto || "")}
                                className={styles.addCommentAvatar}
                            >
                                {!userProfilePhoto && user?.firstName?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box className={styles.commentInputWrapper}>
                                <TextField
                                    id="comment-input-field"
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    variant="standard"
                                    placeholder="Write a comment..."
                                    value={commentText}
                                    onChange={(e) => {
                                        setCommentText(e.target.value);
                                        if (commentError) setCommentError(false);
                                    }}
                                    error={commentError}
                                    InputProps={{
                                        disableUnderline: true,
                                        className: styles.commentTextField,
                                    }}
                                    sx={{ padding: "6px 0" }}
                                />
                                <IconButton
                                    size="small"
                                    className={styles.inputActionIcon}
                                    onClick={handleAddComment}
                                    disabled={!commentText.trim() || isSubmitting}
                                >
                                    {isSubmitting && isCommentLoading ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <SendOutlinedIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>

            <SharePopup
                url={currentURL}
                anchorEl={shareAnchorEl}
                onClose={handleCloseSharePopover}
            />

            <Menu
                anchorEl={postMenuAnchorEl}
                open={Boolean(postMenuAnchorEl)}
                onClose={handlePostMenuClose}
                PaperProps={{ className: styles.postMenuPaper }}
            >
                <MenuItem
                    onClick={handleOpenDeletePostDialog}
                    className={`${styles.commentMenuItem} ${styles.deleteAction}`}
                >
                    <ListItemIcon>
                        <DeleteForeverIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete Post</ListItemText>
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ className: styles.commentMenuPaper }}
            >
                <MenuItem onClick={handleEditClick} className={styles.commentMenuItem}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit Comment</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => handleOpenDeleteDialog(selectedCommentId || "")}
                    className={`${styles.commentMenuItem} ${styles.deleteAction}`}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete Comment</ListItemText>
                </MenuItem>
            </Menu>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    sx: { borderRadius: 2, padding: 1, backgroundColor: "var(--card-background)" },
                }}
            >
                <DialogTitle
                    sx={{ fontSize: "1.2rem", fontWeight: 600, color: "var(--text-color)" }}
                >
                    Delete Comment
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: "var(--text-secondary-color)" }}>
                        Are you sure you want to delete this comment? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        variant="outlined"
                        sx={{
                            borderColor: "var(--border-color)",
                            color: "var(--text-secondary-color)",
                            "&:hover": {
                                borderColor: "var(--text-color)",
                                backgroundColor: "var(--hover-background)",
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteComment}
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                            bgcolor: "var(--error-600, #d32f2f)",
                            "&:hover": { bgcolor: "var(--error-700, #c62828)" },
                        }}
                        startIcon={
                            isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
                        }
                    >
                        {isSubmitting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <DeletePostModal
                isOpen={isDeleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleConfirmDeletePost}
                isDeleting={isDeleting}
            />
        </Box>
    );
};

export default PostDetail;
